import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon } from '@heroicons/react/24/outline';
import { getEmployeeBgvFileBlobApi } from '../../services/axios/AuthApis/bgvApis';
import FilePreviewModal from './FilePreviewModal';
import toast from "../../utils/notify";
import { DataTable } from '../common/DataTable';

const BgvView = ({
  bgvLoading,
  filteredBgvRecords,
  bgvFilters,
  setBgvFilters,
  hasActiveBgvFilters,
  bgvPagination,
  setBgvPagination,
  getBgvStatusColor,
  user,
}) => {
  const [viewLoading, setViewLoading] = useState(false);
  const uniqueDepartments = [...new Set((filteredBgvRecords || []).map((r) => r.department).filter(Boolean))];
  // { file: Blob, title } while a BGV document is open.
  const [preview, setPreview] = useState(null);

  const handleViewBgvRecord = (employeeId) => async () => {
    if (!employeeId) {
      console.error("Employee ID is required to view BGV record details");
      return;
    }
    try {
      setViewLoading(true);
      // The newest BGV file, streamed through the backend and shown from memory.
      setPreview({ file: await getEmployeeBgvFileBlobApi(employeeId), title: 'BGV Document' });
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.info('No file available for this record');
      } else {
        console.error("Error in handleViewBgvRecord:", err);
        toast.error('Could not open the BGV document');
      }
    } finally {
      setViewLoading(false);
    }
  };

  // Employee/Department/DOJ are filtered client-side on whatever page is
  // currently loaded (filteredBgvRecords is already computed that way in
  // the parent) - pagination itself is still real/server-driven.
  const columns = [
    {
      id: 'employee',
      accessorFn: (r) => r.firstname,
      header: 'Employee',
      enableSorting: false,
      meta: { filter: { variant: 'text', label: 'Employee', placeholder: 'Search employee' } },
      cell: ({ row }) => (
        <span className="font-medium text-gray-900">{row.original.firstname || '—'}</span>
      ),
    },
    {
      id: 'department',
      accessorFn: (r) => r.department,
      header: 'Department',
      enableSorting: false,
      meta: {
        filter: {
          variant: 'combobox',
          label: 'Department',
          placeholder: 'All departments',
          options: uniqueDepartments.map((v) => ({ value: v, label: v })),
          maxVisible: 4,
        },
      },
      cell: ({ row }) => <span className="text-gray-900">{row.original.department || '—'}</span>,
    },
    {
      id: 'doj',
      accessorFn: (r) => r.date_of_joining,
      header: 'DOJ',
      enableSorting: false,
      meta: { filter: { variant: 'date', label: 'DOJ' } },
      cell: ({ row }) => (
        <span className="text-gray-900">
          {row.original.date_of_joining ? new Date(row.original.date_of_joining).toLocaleDateString('en-GB') : '—'}
        </span>
      ),
    },
    {
      id: 'status',
      accessorFn: (r) => r.verification_status,
      header: 'BGV Status',
      enableSorting: false,
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getBgvStatusColor(row.original.verification_status)}`}>
          {row.original.verification_status === 1 ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <button
          type="button"
          className="text-blue-600 hover:text-blue-900"
          title="View"
          onClick={handleViewBgvRecord(row.original.employee_id)}
        >
          <EyeIcon className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const columnFilters = [
    ...(bgvFilters.employee ? [{ id: 'employee', value: bgvFilters.employee }] : []),
    ...(bgvFilters.department ? [{ id: 'department', value: bgvFilters.department }] : []),
    ...(bgvFilters.doj ? [{ id: 'doj', value: bgvFilters.doj }] : []),
  ];

  return (
  <motion.div
    key="bgv-records-table"
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.3 }}
  >
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Background Verification Records</h3>
        <p className="text-sm text-gray-600 mt-1">
          Track employee background verification status and DOJ
          {!bgvLoading && (
            <span className="ml-2 text-xs text-gray-500">
              Showing <span className="font-semibold text-blue-600">{filteredBgvRecords.length}</span> record{filteredBgvRecords.length !== 1 ? "s" : ""}
              {hasActiveBgvFilters && " with active filters"}
            </span>
          )}
        </p>
      </div>

      <DataTable
        columns={columns}
        data={filteredBgvRecords}
        getRowId={(r) => String(r.employee_id)}
        loading={bgvLoading}
        emptyMessage="No BGV records found"
        manualPagination
        rowCount={bgvPagination.count || 0}
        pagination={{ pageIndex: (bgvPagination.page || 1) - 1, pageSize: bgvPagination.page_size }}
        onPaginationChange={(updater) => {
          const currentPagination = { pageIndex: (bgvPagination.page || 1) - 1, pageSize: bgvPagination.page_size };
          const nextPagination = typeof updater === 'function' ? updater(currentPagination) : updater;
          setBgvPagination((p) => ({ ...p, page: nextPagination.pageIndex + 1, page_size: nextPagination.pageSize }));
        }}
        enableColumnVisibility={false}
        enableColumnResizing
        enableColumnFilters
        manualFiltering
        columnFilters={columnFilters}
        onColumnFiltersChange={(updater) => {
          const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
          const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
          setBgvFilters({
            employee: values.employee || '',
            department: values.department || '',
            doj: values.doj || '',
          });
        }}
        className="overflow-hidden"
        maxBodyHeight="65vh"
      />
    </div>

    <FilePreviewModal file={preview?.file} title={preview?.title} onClose={() => setPreview(null)} />
  </motion.div>
)};

export default BgvView;
